import OpenInFullOutlined from '@mui/icons-material/OpenInFullOutlined'
import { Box, IconButton } from '@mui/material'
import type { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { FormattedRow } from '@/features/bessDashboard/types'
import BidLadderInput from '@/features/bidding/components/cascading/bid-ladder/asymmetrical/BidLadderInput'
import ChartSection from '@/features/bidding/components/cascading/bid-ladder/shared/ChartSection'
import ReviewBidsButton from '@/features/bidding/components/cascading/bid-ladder/shared/ReviewBidsButton'
import SectionHeader from '@/features/bidding/components/cascading/bid-ladder/shared/SectionHeader'
import { useResponsiveChartHeight } from '@/features/bidding/components/cascading/bid-ladder/shared/useResponsiveChartHeight'
import BidLadderCharts from '@/features/bidding/components/cascading/charts/BidLadderCharts'
import ExpandChartDialog from '@/features/bidding/components/cascading/charts/ExpandChartDialog'
import HourlyPriceChart from '@/features/bidding/components/cascading/charts/HourlyPriceChart'
import HourlyVolumeChart from '@/features/bidding/components/cascading/charts/HourlyVolumeChart'
import InteractiveHourlyVolumeChart from '@/features/bidding/components/cascading/charts/InteractiveHourlyVolumeChart'
import { CASCADE_MARKETS } from '@/features/bidding/constants'
import type { UseBidLadderInputStateReturn } from '@/features/bidding/hooks/useBidLadderInputState'
import type { UsePricingStrategyStateReturn } from '@/features/bidding/hooks/usePricingStrategyState'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { DragPayload } from '@/features/bidding/types/bidLadderInput'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { cascadeMarketToForecastMarket } from '@/features/bidding/utils/forecast/marketProgramNames'
import { getMarketVolumeGranularity } from '@/features/bidding/utils/marketCapabilities'

type BidLadderContentProps = {
  bidLadderState: UseBidLadderInputStateReturn
  forecastsData: FormattedRow[] | undefined
  selectedDeliveryDay: DateTime
  totalMWAvailable: number
  deliveryDay: MarketDate
  /** Selected market from the toolbar */
  selectedMarket: CascadeMarketId
  /** Aggregate availability to show as overlay lines on the price chart */
  aggregateAvailability?: AggregateAvailabilityResponse | null
  /** Aggregate suggested bids to show as overlay lines on the volume chart */
  aggregateSuggestedBids?: AggregateSuggestedBidResponse | null
  /** Pricing strategy configuration for forecast-based pricing */
  pricingStrategy?: UsePricingStrategyStateReturn
  /** Whether drag and drop is enabled for this market (defaults to true for mFRR) */
  isDragDropEnabled?: boolean
  onDragStartFromLadder?: (payload: DragPayload) => void
  onDragEndFromLadder?: () => void
  /** Called when clicking forecast chip to open pricing configuration in sidebar */
  onOpenPricingConfiguration?: () => void
  /** Number of activation groups available (used by ReviewBidsButton to determine disabled state) */
  activationGroupsCount: number
  /** Called when clicking Review Bids button */
  onReviewBids?: () => void
  /** Excluded PTU groups for per-group per-hour toggling (mFRR only) */
  excludedPtuGroups?: Set<string>
  /** Called when toggling a group exclusion on the interactive volume chart */
  onTogglePtuGroup?: (groupUuid: string, hour: number) => void
}

function BidLadderContent({
  bidLadderState,
  forecastsData,
  selectedDeliveryDay,
  totalMWAvailable,
  deliveryDay,
  selectedMarket,
  aggregateAvailability,
  aggregateSuggestedBids,
  pricingStrategy,
  isDragDropEnabled = true,
  onDragStartFromLadder,
  onDragEndFromLadder,
  onOpenPricingConfiguration,
  activationGroupsCount,
  onReviewBids,
  excludedPtuGroups,
  onTogglePtuGroup,
}: Readonly<BidLadderContentProps>) {
  const { t } = useTranslation()
  const { overviewChartHeight } = useResponsiveChartHeight()
  const [expandedChart, setExpandedChart] = useState<'price' | 'overview' | 'volume' | null>(null)

  // Format delivery date for display
  const formattedDeliveryDate = deliveryDay.getStartOfDay().toFormat('EEE, MMM d')

  const forecastMarket = cascadeMarketToForecastMarket(selectedMarket)
  const marketLabel = CASCADE_MARKETS.find((m) => m.id === selectedMarket)?.label ?? selectedMarket
  const fixedModeDecimalPlaces = getMarketVolumeGranularity(selectedMarket).decimalPlaces

  const hourlyVolumeChart =
    excludedPtuGroups && onTogglePtuGroup ? (
      <InteractiveHourlyVolumeChart
        bidLevelInputs={bidLadderState.levels}
        excludedPtuGroups={excludedPtuGroups}
        market={forecastMarket}
        selectedDeliveryDay={selectedDeliveryDay}
        onTogglePtuGroup={onTogglePtuGroup}
      />
    ) : (
      <HourlyVolumeChart
        aggregateAvailability={aggregateAvailability}
        aggregateSuggestedBids={aggregateSuggestedBids}
        bidLevelInputs={bidLadderState.levels}
        market={forecastMarket}
        selectedDeliveryDay={selectedDeliveryDay}
      />
    )

  // Count assigned activation groups
  const assignedGroupsCount = useMemo(() => {
    let count = 0
    for (const level of bidLadderState.levels) {
      count += level.assignedGroups.length
    }
    return count
  }, [bidLadderState.levels])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'grey.50',
      }}
    >
      {/* Scrollable charts area */}
      <Box
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          overflow: 'auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {/* Hourly Price Section */}
        <ChartSection
          action={
            <IconButton size="small" onClick={() => setExpandedChart('price')}>
              <OpenInFullOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          }
          minHeight={280}
          subtitle={`${formattedDeliveryDate} · ${t('bidding.bidding_cascading.bid_ladder.hourly_price_subtitle')}`}
          title={t('bidding.bidding_cascading.bid_ladder.hourly_price_title')}
        >
          <HourlyPriceChart
            bidLevelInputs={bidLadderState.levels}
            forecastsData={forecastsData}
            market={cascadeMarketToForecastMarket(selectedMarket)}
            pricingConfig={pricingStrategy?.config}
            selectedDeliveryDay={selectedDeliveryDay}
          />
        </ChartSection>

        {/* Average Bid Curve Section */}
        <ChartSection
          action={
            <IconButton size="small" onClick={() => setExpandedChart('overview')}>
              <OpenInFullOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          }
          contentBackgroundColor="grey.50"
          minHeight={overviewChartHeight}
          subtitle={`${formattedDeliveryDate} · ${t('bidding.bidding_cascading.bid_ladder.preview_subtitle')}`}
          title={t('bidding.bidding_cascading.bid_ladder.preview_title')}
        >
          <BidLadderCharts
            aggregateAvailability={aggregateAvailability}
            aggregateSuggestedBids={aggregateSuggestedBids}
            bidLevelInputs={bidLadderState.levels}
            forecastsData={forecastsData}
            height={overviewChartHeight}
            market={cascadeMarketToForecastMarket(selectedMarket)}
            pricingConfig={pricingStrategy?.config}
            selectedDeliveryDay={selectedDeliveryDay}
            totalMWAvailable={totalMWAvailable}
          />
        </ChartSection>

        {/* Hourly Volume Section */}
        <ChartSection
          action={
            <IconButton size="small" onClick={() => setExpandedChart('volume')}>
              <OpenInFullOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          }
          minHeight={250}
          showBorderBottom={false}
          subtitle={`${formattedDeliveryDate} · ${t('bidding.bidding_cascading.bid_ladder.hourly_volume_subtitle')}`}
          title={t('bidding.bidding_cascading.bid_ladder.hourly_volume_title', { market: marketLabel })}
        >
          {hourlyVolumeChart}
        </ChartSection>
      </Box>

      {/* Fixed bottom: Bid Levels Input + Actions */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          position: 'relative',
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'divider',
            boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.06)',
          },
        }}
      >
        {/* Bid Levels Input Section */}
        <SectionHeader
          subtitle={`${bidLadderState.levels.length} levels · ${assignedGroupsCount} groups`}
          title={t('bidding.bidding_cascading.bid_ladder.title')}
        />
        <Box
          sx={{
            backgroundColor: 'background.paper',
          }}
        >
          <BidLadderInput
            assignGroups={bidLadderState.assignGroups}
            fixedModeDecimalPlaces={fixedModeDecimalPlaces}
            getTotalMW={bidLadderState.getTotalMW}
            getTotalMWAcrossAllLevels={bidLadderState.getTotalMWAcrossAllLevels}
            isDragDropEnabled={isDragDropEnabled}
            levels={bidLadderState.levels}
            pricingStrategy={onOpenPricingConfiguration ? pricingStrategy : undefined}
            showTotalMW={selectedMarket === 'mfrr'}
            unassignRegulationObjectGroup={bidLadderState.unassignRegulationObjectGroup}
            updatePrice={bidLadderState.updatePrice}
            updateVolumeCapacity={bidLadderState.updateVolumeCapacity}
            onDragEndFromLadder={isDragDropEnabled ? onDragEndFromLadder : undefined}
            onDragStartFromLadder={isDragDropEnabled ? onDragStartFromLadder : undefined}
            onOpenPricingConfiguration={onOpenPricingConfiguration}
          />
        </Box>

        {/* Review Bids Button */}
        <ReviewBidsButton
          activationGroupsCount={activationGroupsCount}
          aggregateAvailability={aggregateAvailability}
          aggregateSuggestedBids={aggregateSuggestedBids}
          levels={bidLadderState.levels}
          selectedMarket={selectedMarket}
          onReviewBids={onReviewBids}
        />
      </Box>

      {/* Expanded Chart Dialogs */}
      <ExpandChartDialog
        open={expandedChart === 'price'}
        title={t('bidding.bidding_cascading.bid_ladder.hourly_price_title')}
        onClose={() => setExpandedChart(null)}
      >
        <HourlyPriceChart
          bidLevelInputs={bidLadderState.levels}
          forecastsData={forecastsData}
          height={600}
          market={cascadeMarketToForecastMarket(selectedMarket)}
          pricingConfig={pricingStrategy?.config}
          selectedDeliveryDay={selectedDeliveryDay}
        />
      </ExpandChartDialog>

      <ExpandChartDialog
        open={expandedChart === 'overview'}
        title={t('bidding.bidding_cascading.bid_ladder.preview_title')}
        onClose={() => setExpandedChart(null)}
      >
        <BidLadderCharts
          aggregateAvailability={aggregateAvailability}
          aggregateSuggestedBids={aggregateSuggestedBids}
          bidLevelInputs={bidLadderState.levels}
          forecastsData={forecastsData}
          height={600}
          market={cascadeMarketToForecastMarket(selectedMarket)}
          pricingConfig={pricingStrategy?.config}
          selectedDeliveryDay={selectedDeliveryDay}
          totalMWAvailable={totalMWAvailable}
        />
      </ExpandChartDialog>

      <ExpandChartDialog
        open={expandedChart === 'volume'}
        title={t('bidding.bidding_cascading.bid_ladder.hourly_volume_title', { market: marketLabel })}
        onClose={() => setExpandedChart(null)}
      >
        {excludedPtuGroups && onTogglePtuGroup ? (
          <InteractiveHourlyVolumeChart
            bidLevelInputs={bidLadderState.levels}
            excludedPtuGroups={excludedPtuGroups}
            height={600}
            market={forecastMarket}
            selectedDeliveryDay={selectedDeliveryDay}
            onTogglePtuGroup={onTogglePtuGroup}
          />
        ) : (
          <HourlyVolumeChart
            aggregateAvailability={aggregateAvailability}
            aggregateSuggestedBids={aggregateSuggestedBids}
            bidLevelInputs={bidLadderState.levels}
            height={600}
            market={forecastMarket}
            selectedDeliveryDay={selectedDeliveryDay}
          />
        )}
      </ExpandChartDialog>
    </Box>
  )
}

export default BidLadderContent
